import { isValidElement } from "react";
import type React from "react";

/**
 * React 노드에서 표시 텍스트만 추출한다. MDX 렌더 계열(CodeBlock 등)이 공유한다.
 */

/** 텍스트에서 통째로 제외할 요소인지 판별한다. */
export type ExcludePredicate = (node: React.ReactNode) => boolean;

/**
 * 컴포넌트 이름·정적 마커로 판별하는 기본 휴리스틱.
 *
 * **서버(RSC)에서는 믿을 수 없다.** "use client" 컴포넌트가 서버 컴포넌트로 들어오면
 * `type` 이 클라이언트 참조 프록시라서 displayName/name 이 비고(JCG-2144), 임의 속성
 * 접근이 예외를 던지는 경우도 있다. 그래서 접근을 try 로 감싸고, 서버 경로에서는
 * 호출부가 `isExcluded` 로 참조 동일성 검사를 주입해야 한다.
 * 클라이언트에서는 `type` 이 실제 함수라 이 검사가 정상 동작한다.
 */
function looksLikeSectionLevel(node: React.ReactNode): boolean {
  if (!isValidElement(node)) return false;
  const type = node.type;
  if (typeof type !== "function" && typeof type !== "object") return false;
  try {
    const t = type as {
      displayName?: string;
      name?: string;
      isSectionLevel?: boolean;
    };
    if (t.isSectionLevel) return true;
    const name = t.displayName ?? t.name;
    return name === "SectionLevel";
  } catch {
    // 클라이언트 참조 프록시는 dot 접근에 예외를 던질 수 있다. 판별 실패로 본다.
    return false;
  }
}

export function extractText(
  node: React.ReactNode,
  /**
   * 제외 판별 주입. 서버에서 렌더하는 앱은 자기 컴포넌트를 참조 동일성
   * (`type === MyComponent`)으로 검사하는 함수를 넘겨야 한다 — 위 휴리스틱은
   * 그 경로에서 실패한다.
   */
  isExcluded: ExcludePredicate = looksLikeSectionLevel,
): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (node === null || node === undefined) return "";

  if (Array.isArray(node)) {
    return node.map((child) => extractText(child, isExcluded)).join("");
  }

  if (isValidElement(node)) {
    // SectionLevel 뱃지는 레벨 텍스트(쌩기초/기초/심화/호기심)가 섞이지 않도록 제외
    if (isExcluded(node)) return "";
    const props = node.props as Record<string, unknown>;

    // children이 있으면 재귀적으로 탐색
    if (props && "children" in props) {
      return extractText(props.children as React.ReactNode, isExcluded);
    }

    // children이 없지만 다른 props에 텍스트가 있을 수 있음
    const allTextValues: string[] = [];
    for (const [key, value] of Object.entries(props || {})) {
      if (typeof value === "string" && key !== "className") {
        allTextValues.push(value);
      } else if (typeof value === "number") {
        allTextValues.push(String(value));
      }
    }
    return allTextValues.join("");
  }

  return "";
}
